// src/Container/Signup/signup.js

import React, { useState, useRef, useEffect } from 'react';
import {
  Button,
  TextField,
  Grid,
  Box,
  Typography,
  createTheme,
  ThemeProvider,
  InputAdornment,
  IconButton,
  CircularProgress,
  Dialog,
  DialogContent,
} from '@mui/material';

import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

import {
  createUserWithEmailAndPassword,
  updateProfile,
  signInWithPopup,
  signOut,
  getAdditionalUserInfo,
  deleteUser,
} from 'firebase/auth';

import { httpsCallable } from 'firebase/functions';
import { auth, provider, db, functions } from '../../Firebase/firebase';
import {
  doc,
  getDoc,
  writeBatch,
  collection,
  query,
  where,
  getDocs,
} from 'firebase/firestore';
import AlertBox from '../../Components/Alert/alert';
import ConfirmDialog from '../../Components/ConfirmDialog/ConfirmDialog';
import { getFriendlyError } from '../../utils/firebaseErrors';

import GooogleImage from '../../assets/images/Google-G.png';
import CloseIcon from '@mui/icons-material/Close';

const theme = createTheme();

export default function Signup({ open, onClose, onSwitchToLogin, onSuccess }) {
  const [userInfo, setUserInfo] = useState({
    userName: '',
    companyName: '',
    email: '',
    password: '',
  });

  const [showPassword, setShowPassword] = useState(false);
  const [alert, setAlert] = useState({ visible: false, severity: '', message: '' });
  const [loadingAction, setLoadingAction] = useState(null);

  const [existsDialogOpen, setExistsDialogOpen] = useState(false);
  const [permDeniedDialog, setPermDeniedDialog] = useState(false);

  const timerRef = useRef(null);

  useEffect(() => {
    signOut(auth).catch((err) => {});
    return () => clearTimeout(timerRef.current);
  }, []);

  useEffect(() => {
    if (!open) {
      setUserInfo({ userName: '', companyName: '', email: '', password: '' });
      setLoadingAction(null);
    }
  }, [open]);

  const showTemporaryAlert = (severity, message) => {
    setAlert({ visible: true, severity, message });
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setAlert({ visible: false, severity: '', message: '' });
    }, 3000);
  };

  const checkPermittedUser = async (email) => {
    const permRef = doc(db, 'permittedUsers', email.toLowerCase().trim());
    const permSnap = await getDoc(permRef);
    return permSnap.exists();
  };

  const addToCollections = async (user, displayName, companyName = '') => {
    const batch = writeBatch(db);
    const email = user.email.toLowerCase().trim();
    const date = new Date().toISOString();

    try {
      const q = query(collection(db, 'mailingList'), where('email', '==', email));
      const snapshot = await getDocs(q);

      snapshot.forEach((docSnap) => {
        if (docSnap.id !== user.uid) {
          batch.delete(docSnap.ref);
        }
      });

      const unsubQ = query(
        collection(db, 'unsubscribeRequests'),
        where('email', '==', email),
      );
      const unsubSnap = await getDocs(unsubQ);

      unsubSnap.forEach((docSnap) => {
        batch.delete(docSnap.ref);
      });
    } catch (queryError) {
      console.warn('Could not query collections. Proceeding with writes.', queryError);
    }

    const mailingRef = doc(db, 'mailingList', user.uid);
    batch.set(
      mailingRef,
      {
        userName: displayName || '',
        companyName: companyName || '',
        email: email,
        addedAt: date,
      },
      { merge: true },
    );

    try {
      await batch.commit();
    } catch (commitError) {
      console.error('CRITICAL ERROR writing to collections:', commitError);
      throw commitError;
    }
  };

  const performSignup = async (emailToUse) => {
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      emailToUse,
      userInfo.password,
    );
    const user = userCredential.user;

    // AMENDED: Check permission NOW that the user is authenticated
    // to bypass restrictive Firestore rules.
    const isPermitted = await checkPermittedUser(emailToUse);
    if (!isPermitted) {
      await deleteUser(user);
      const err = new Error('NOT_PERMITTED');
      err.code = 'NOT_PERMITTED';
      throw err;
    }

    await updateProfile(user, {
      displayName: userInfo.userName,
      photoURL: null,
    });

    await addToCollections(user, userInfo.userName, userInfo.companyName);

    // Explicitly trigger the verification email now that the profile is fully built
    // This prevents the race condition that triggers the "Hello, New User!" phishing heuristic
    try {
      const sendVerification = httpsCallable(functions, 'resendVerificationEmail');
      await sendVerification();
    } catch (emailError) {
      console.error('Failed to explicitly trigger OTP dispatch:', emailError);
    }

    onSuccess(null);
  };

  const handleSubmit = async (event) => {
    if (event && event.preventDefault) event.preventDefault();
    if (!userInfo.userName || !userInfo.email || !userInfo.password) {
      showTemporaryAlert('error', 'Please fill all required fields');
      return;
    }

    setLoadingAction('EMAIL');
    const email = userInfo.email.toLowerCase().trim();

    try {
      await performSignup(email);
    } catch (error) {
      // Custom catch for our manually thrown permission error
      if (error.code === 'NOT_PERMITTED' || error.message === 'NOT_PERMITTED') {
        setLoadingAction(null);
        setPermDeniedDialog(true);
        return;
      }

      if (error.code === 'auth/email-already-in-use') {
        try {
          const deleteUnverified = httpsCallable(functions, 'deleteUnverifiedAccount');
          await deleteUnverified({ email });
          await performSignup(email);
        } catch (backendError) {
          // Catch permission error if it occurs during the retry attempt
          if (
            backendError.code === 'NOT_PERMITTED' ||
            backendError.message === 'NOT_PERMITTED'
          ) {
            setLoadingAction(null);
            setPermDeniedDialog(true);
            return;
          }

          try {
            const checkAccount = httpsCallable(functions, 'checkAccountStatus');
            const statusResult = await checkAccount({ email });
            const { exists, providers } = statusResult.data;

            if (
              exists &&
              providers.includes('google.com') &&
              !providers.includes('password')
            ) {
              const sendLinkAccountOtp = httpsCallable(functions, 'sendLinkAccountOtp');
              await sendLinkAccountOtp({ email });

              setLoadingAction(null);
              onSuccess({
                email: email,
                password: userInfo.password,
                userName: userInfo.userName,
                companyName: userInfo.companyName,
              });
              return;
            }
          } catch (checkErr) {
            console.error('Error checking account status:', checkErr);
          }

          setLoadingAction(null);
          setExistsDialogOpen(true);
          setTimeout(() => {
            onSwitchToLogin();
          }, 2500);
        }
      } else {
        setLoadingAction(null);
        showTemporaryAlert('error', getFriendlyError(error));
      }
    }
  };

  const handleGoogleButton = async () => {
    setLoadingAction('GOOGLE');
    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      const email = user.email.toLowerCase().trim();

      const isPermitted = await checkPermittedUser(email);

      if (!isPermitted) {
        const additionalInfo = getAdditionalUserInfo(result);
        if (additionalInfo && additionalInfo.isNewUser) {
          await deleteUser(user);
        } else {
          await signOut(auth);
        }
        setLoadingAction(null);
        setPermDeniedDialog(true);
        return;
      }

      await addToCollections(user, user.displayName, '');
      onClose();
    } catch (error) {
      setLoadingAction(null);
      showTemporaryAlert('error', getFriendlyError(error));
    }
  };

  const inputStyle = {
    '& .MuiInputBase-root': { height: 40 },
    '& .MuiInputLabel-root': { transform: 'translate(14px, 10px) scale(1)' },
    '& .MuiInputLabel-shrink': { transform: 'translate(14px, -6px) scale(0.75)' },
  };

  return (
    <ThemeProvider theme={theme}>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth='xs'
        fullWidth
        sx={{ zIndex: 100000 }}
        PaperProps={{
          sx: { borderRadius: 3, overflow: 'visible', position: 'relative' },
        }}
        BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.2)' } }}>
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', top: 8, right: 8, zIndex: 100 }}>
          <CloseIcon />
        </IconButton>

        <DialogContent
          sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <ConfirmDialog
            open={existsDialogOpen}
            onClose={() => setExistsDialogOpen(false)}
            message='You already have an account. Please Sign In.'
            severity='info'
            autoClose={2000}
            sx={{ zIndex: 100005 }}
          />

          <ConfirmDialog
            open={permDeniedDialog}
            onClose={() => setPermDeniedDialog(false)}
            onConfirm={() => setPermDeniedDialog(false)}
            message='You do not have permission to Sign Up. See Admin for instructions.'
            severity='warning'
            sx={{ zIndex: 100005 }}
          />

          <div
            style={{
              position: 'absolute',
              top: -60,
              left: 0,
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              zIndex: 100005,
            }}>
            <AlertBox
              visible={alert.visible}
              severity={alert.severity}
              message={alert.message}
            />
          </div>

          <Typography component='h1' variant='h5' fontWeight='bold' sx={{ mb: 1 }}>
            Sign up
          </Typography>

          <Box
            component='form'
            noValidate
            onSubmit={handleSubmit}
            sx={{ mt: 1, width: '100%' }}>
            <TextField
              size='small'
              margin='dense'
              required
              fullWidth
              id='name'
              label='Name'
              name='userName'
              autoComplete='name'
              autoFocus
              disabled={loadingAction !== null}
              value={userInfo.userName}
              onChange={(e) => setUserInfo({ ...userInfo, userName: e.target.value })}
              sx={inputStyle}
            />

            <TextField
              size='small'
              margin='dense'
              fullWidth
              id='company'
              label='Company'
              name='companyName'
              autoComplete='organization'
              disabled={loadingAction !== null}
              value={userInfo.companyName}
              onChange={(e) => setUserInfo({ ...userInfo, companyName: e.target.value })}
              sx={inputStyle}
            />

            <TextField
              size='small'
              margin='dense'
              required
              fullWidth
              id='email'
              label='Email Address'
              name='email'
              autoComplete='email'
              disabled={loadingAction !== null}
              value={userInfo.email}
              onChange={(e) => setUserInfo({ ...userInfo, email: e.target.value })}
              sx={inputStyle}
            />

            <TextField
              size='small'
              margin='dense'
              required
              fullWidth
              name='password'
              label='Password'
              type={showPassword ? 'text' : 'password'}
              id='password'
              autoComplete='new-password'
              disabled={loadingAction !== null}
              value={userInfo.password}
              onChange={(e) => setUserInfo({ ...userInfo, password: e.target.value })}
              sx={inputStyle}
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      size='small'
                      disabled={loadingAction !== null}>
                      {showPassword ? (
                        <VisibilityOff fontSize='small' />
                      ) : (
                        <Visibility fontSize='small' />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type='submit'
              fullWidth
              variant='contained'
              disabled={loadingAction !== null}
              sx={{
                mt: 2,
                mb: 1,
                py: 1,
                height: '36px',
                backgroundColor: '#1976d2',
                '&:hover': {
                  backgroundColor: '#1565c0',
                  transform: 'scale(1.02)',
                },
                '&:active': {
                  backgroundColor: '#1976d2',
                },
                '&.Mui-disabled': {
                  backgroundColor: '#1976d2',
                  color: '#fff',
                },
              }}>
              {loadingAction === 'EMAIL' && (
                <CircularProgress size={20} sx={{ color: 'white', mr: 1 }} />
              )}
              <span>Sign Up</span>
            </Button>

            <Button
              onClick={handleGoogleButton}
              fullWidth
              variant='outlined'
              disabled={loadingAction !== null}
              sx={{
                mb: 1,
                backgroundColor: '#FFFFFF',
                color: '#3c4043',
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '16px',
                borderRadius: '4px',
                borderColor: '#dadce0',
                paddingY: 0,
                boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1.2,
                '&:hover': {
                  backgroundColor: '#f7f8f8',
                  borderColor: '#d2d2d2',
                },
                '&:active': {
                  backgroundColor: '#FFFFFF',
                },
                '&.Mui-disabled': {
                  backgroundColor: '#FFFFFF',
                  borderColor: '#dadce0',
                  color: '#3c4043',
                },
              }}>
              {loadingAction === 'GOOGLE' ? (
                <CircularProgress size={20} sx={{ color: '#3c4043' }} />
              ) : (
                <img
                  src={GooogleImage}
                  alt='Google'
                  style={{ width: 40, height: 40, objectFit: 'contain' }}
                />
              )}
              <span>Sign up with Google</span>
            </Button>

            <Grid container justifyContent='flex-end'>
              <Grid item>
                <Typography
                  onClick={onSwitchToLogin}
                  sx={{
                    color: '#1976d2',
                    fontSize: '0.875rem',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    pointerEvents: loadingAction !== null ? 'none' : 'auto',
                    opacity: loadingAction !== null ? 0.5 : 1,
                  }}>
                  Already have an account? Sign in
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
      </Dialog>
    </ThemeProvider>
  );
}
